import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Dialog, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@material-ui/icons/Close';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { toast } from '../../../utils/utils';
import { TOAST_TYPES, DQ_PRO_PLAN_KEY, PRODUCT_KEY } from '../../../utils/constants';
import { canCreateProduct, solutionPlanKey } from '../../../utils/productAccess';
import { listOrgSchoolsService, listOrgClassesService, createProgramRequestService, getOrgDetailsService } from '../../../services/client.service';
import { findCountry, useGeo } from '../../../utils/geo';
import UnlockCapabilityDialog from '../DQProV2/components/UnlockCapabilityDialog.comp';
import RequestLearningProgramsWizard from '../DQProgramCreator/components/shared/RequestLearningProgramsWizard.comp';
import WorkspacePlaceholder from '../../MiscPages/WorkspacePlaceholder.page';
import {openLearnCreator} from '../../../utils/learncreator';
import { getSchoolDashboardService, getSchoolNetworkDashboardService } from '../../../services/dynamicDashboard.service';
import { findCard, rowsFrom, orgColumn, whole } from '../../NetworkDashboard/networkData';
import { EvidenceCell, RecommendationCell } from '../../DashboardCommon/RecommendationCells.comp';

/**
 * School Executive Overview — "Performance Summary" (School + Network
 * workspaces share this one route/component, same as the placeholder it
 * replaces). Nav sheet code: SW/SNW-{P0|P1A|P1B|LC}-Performance Summary.
 *
 * P0, P1A, P1B and LC. Every other plan/tier (including no plan at all)
 * falls back to the generic WorkspacePlaceholder.
 *
 * Real plan_keys are only p0 / p1 / lc (DQ_PRO_PLAN_KEY.ENTERPRISE_P0/P1/LC)
 * — P1A vs P1B is NOT a plan_key, both carry enterprise_p1. isP1A/isP1B below
 * match DQProLearnerProgramList.page.jsx's own isP1AAccount exactly: plan_key
 * === ENTERPRISE_P1 AND canCreateProduct(authUser, PRODUCT_KEY.LEARNING_PROGRAM)
 * — create: true is P1B (can build its own programs via LearnCreator);
 * create: false/absent is P1A (requests programs from its partner instead).
 *
 * LIVE DATA (LA-SW-05Oct-2, LA-SNW-05Oct-2): the dashboard API's own
 * Performance Summary section, fetched here directly (not via
 * DashboardContext, which only loads on dashboard routes) — the School
 * dashboard for a school, the School Network dashboard for a network:
 *  - info bar + Baseline cards   SD.S_4 / SND.S_3 (Executive Summary)
 *  - Context & Support, Reassess, COS Movement   SD.S_17 / SND.S_11
 *    (the context composite's baseline; Reassessment Duration; the three
 *    MovementCards)
 *  - Learning Impact cards + both recommendation tables   SD.S_22 / SND.S_16
 *    ("PERFORMANCE SUMMARY / LEARNING IMPACT", written for this page)
 * A value the data does not carry (the Learning date, a single school's
 * country, Priority Curriculum Coverage) or hides under the n < 10 rule
 * reads "—" — never a sample figure.
 *  - P0's "Take Action" opens the already-built UnlockCapabilityDialog (per
 *    the mockup's own "SW-P0-UnlockRequest"/"SNW-P0-UnlockRequest" annotations
 *    — same dialog for both workspaces) — real, working component, though its
 *    own submission step is itself still a stub (see that component's doc
 *    comment, Gap Register G005).
 *  - Every tier (P0, P1A, P1B, LC) has its own SNW-*-Performance Summary
 *    mockup, all showing the identical Network Executive Overview treatment
 *    on a School Network account (authUser.is_school_network): "Network
 *    Executive Overview" title and a NETWORK badge in the info bar in place
 *    of a school name. showNetworkVariant is therefore just isNetwork, not
 *    scoped by tier. P0/P1A/P1B additionally gain a Region + School pair of
 *    columns on both recommendation tables instead of one Target Group
 *    column — the mockups' own annotation says a network shows 3
 *    WHOLE-SCHOOL-level rows per table (no grade/class breakdown, unlike the
 *    single-school view); LC never renders those tables either way. The
 *    Learning Impact row's second card also reads "Schools Assigned" on a
 *    network instead of "Classes Assigned" (same 7/10 value) — every network
 *    mockup shows this swap. The network name has no concrete sample in any
 *    mockup (just the template token "{{Network Name}}"), so it uses dummy
 *    data ("Greenfield Schools Network") per explicit instruction, matching
 *    the single-school view's own "Greenfield Academy".
 *  - P1A's "Take Action" opens the already-built RequestLearningProgramsWizard
 *    (per "SW/SNW-P1A-Performance Summary-Take Action") — same component,
 *    same wizardSchools/wizardClasses/wizardGrades loading, and the same
 *    sendProgramRequests submission DQProLearnerProgramList.page.jsx already
 *    uses for its own Request Programs wizard. This IS wired to a real
 *    backend call (createProgramRequestService).
 *  - P1B's "Take Action" opens CreateLearningProgramDialog (per
 *    "SW/SNW-P1B-Performance Summary-Take Action") — a P1B account can
 *    already build programs itself, so instead of requesting one it is sent
 *    straight to the real program builder at /dq_pro/program/creation/preview.
 *  - LC shows the info bar and Learning Impact cards only — no Baseline, no
 *    recommendation tables, no Take Action, no COS Movement. The mockup marks
 *    those sections "Mute" for LC; per explicit instruction that means the
 *    sections are omitted outright, not rendered as an empty/greyed box.
 *  - "View Full List" opens the matching Priority Actions page of the
 *    dashboard — School Dashboard for a school, Network Dashboard for a
 *    network: Learning Program for the first table, System Intervention for
 *    the second.
 *
 * Styling follows this app's own brand tokens (--brand-primary/--brand-
 * secondary/--brand-surface/etc., defined in buildTheme.js) and the card/
 * pill/badge conventions already established on SolutionsOverview.page.jsx
 * and DQProLearnerProgramList.page.jsx, not the mockups' own sample colors —
 * including the mockups' green labels/values on the Learning Impact / COS
 * Movement rows and the green "Take Action"/"Open LearnCreator" buttons,
 * none of which are carried over (no green anywhere in this app's brand
 * accent; orange throughout, same as every other button on this page).
 */

const ORANGE = 'var(--brand-primary, #FF6900)';
const MUTED = 'var(--brand-text-muted, #6B7280)';
const BORDER = 'var(--brand-border, #E5E7EB)';
const TEXT = 'var(--brand-text, #111827)';
const SURFACE = 'var(--brand-surface, #ffffff)';
const CARD_SHADOW = '0px 2px 8px rgba(0,0,0,0.04)';

/** The dashboard sections this page reads, per workspace (see the file header). */
const SECTIONS = {
    school:  { overview: 'S_4', reassess: 'S_17', summary: 'S_22' },
    network: { overview: 'S_3', reassess: 'S_11', summary: 'S_16' },
};

/** A KPI value as text: numbers whole (with % where asked), strings as sent, "—" when empty. */
const kpiText = (v, pct = false) => {
    const n = whole(v);
    if (n !== null && typeof v !== 'string') return pct ? `${n}%` : String(n);
    if (v === null || v === undefined || String(v).trim() === '') return '—';
    return String(v);
};

const infoLabelSx = {
    fontSize: '10.5px',
    fontWeight: 700,
    color: MUTED,
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
};
const infoValueSx = {
    fontSize: '14.5px',
    fontWeight: 700,
    color: TEXT,
    mt: 0.4,
};

const eyebrowSx = {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    color: MUTED,
};

const primaryBtnSx = {
    backgroundColor: ORANGE,
    color: '#fff',
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '13.5px',
    borderRadius: '50px',
    px: 2.5,
    py: 0.9,
    boxShadow: 'none',
    whiteSpace: 'nowrap',
    '&:hover': { backgroundColor: '#e55e00' },
};

const outlineBtnSx = {
    borderColor: ORANGE,
    color: ORANGE,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '13.5px',
    borderRadius: '50px',
    px: 2.5,
    py: 0.9,
    whiteSpace: 'nowrap',
    '&:hover': { borderColor: '#e55e00', backgroundColor: 'rgba(255, 105, 0, 0.06)' },
};

const cardSx = {
    border: `1px solid ${BORDER}`,
    borderRadius: '12px',
    boxShadow: CARD_SHADOW,
    backgroundColor: SURFACE,
};

const tableColumnsSx = (colCount) => ({
    display: 'grid',
    gridTemplateColumns: colCount === 4 ? '1fr 1fr 2fr 2fr' : '1fr 2fr 2fr',
    columnGap: 2, alignItems: 'center', minWidth: colCount === 4 ? 760 : 640,
});

/**
 * Rows from the summary section's priority tables (rowsFrom): a school's start
 * with its Target Group, a network's with Region + School; then the Baseline
 * Evidence and the recommendation, drawn as the Priority Actions pages draw
 * them (EvidenceCell / RecommendationCell).
 */
const labelCellsFor = (row, isNetwork) => (isNetwork ? [row.region, row.school] : [row.targetGroup]);

const RecommendationTable = ({ columns, rows, isNetwork }) => {
    const colSx = tableColumnsSx(columns.length);
    return (
        <Box sx={{ width: '100%', overflowX: 'auto', border: `1px solid ${BORDER}`, borderRadius: '10px' }}>
            <Box sx={{
                ...colSx,
                backgroundColor: 'var(--brand-bg, #f8f9fa)', borderRadius: '10px 10px 0 0',
                borderBottom: `2px solid ${BORDER}`,
            }}>
                {columns.map((c) => (
                    <Typography
                        key={c}
                        sx={{
                            fontSize: '11.5px', fontWeight: 800, color: MUTED,
                            letterSpacing: '0.5px', textTransform: 'uppercase',
                            p: '14px 20px',
                        }}
                    >
                        {c}
                    </Typography>
                ))}
            </Box>
            {rows.length === 0 && (
                <Typography sx={{ fontSize: '13.5px', color: MUTED, p: '18px 20px', textAlign: 'center' }}>
                    No recommendations yet.
                </Typography>
            )}
            {rows.map((row, i) => {
                const labelCells = labelCellsFor(row, isNetwork);
                return (
                    <Box
                        key={`${labelCells.join('|')}-${i}`}
                        sx={{
                            ...colSx,
                            minHeight: 56,
                            borderBottom: i < rows.length - 1 ? `1px solid ${BORDER}` : 'none',
                            borderRadius: i === rows.length - 1 ? '0 0 10px 10px' : 0,
                            transition: 'background-color 120ms',
                            '&:hover': { backgroundColor: 'var(--brand-bg, #FFFBF7)' },
                        }}
                    >
                        {labelCells.map((cell, ci) => (
                            <Typography key={`l-${ci}`} sx={{ fontSize: '13.5px', fontWeight: 700, color: TEXT, p: '14px 20px' }}>
                                {cell}
                            </Typography>
                        ))}
                        <Box sx={{ fontSize: '13.5px', color: TEXT, p: '14px 20px' }}>
                            <EvidenceCell value={row._raw?.evidence} fallback={row.evidence}/>
                        </Box>
                        <Box sx={{ fontSize: '13.5px', color: TEXT, p: '14px 20px' }}>
                            <RecommendationCell row={row}/>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

/**
 * Shared by the Baseline row (P0 and P1A) and P1A's Learning Impact / COS
 * Movement rows — same card look throughout, no per-row color logic.
 */
const StatCardsRow = ({ eyebrow, cards }) => (
    <Box>
        <Typography sx={{ ...eyebrowSx, mb: 1.5 }}>{eyebrow}</Typography>
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 2,
        }}>
            {cards.map((s) => (
                <Box key={s.label} sx={{ ...cardSx, p: 2.5, position: 'relative', overflow: 'hidden' }}>
                    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, backgroundColor: ORANGE }} />
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase', color: MUTED, mb: 1.25 }}>
                        {s.label}
                    </Typography>
                    <Typography sx={{ fontSize: '28px', fontWeight: 800, color: TEXT, lineHeight: 1.1 }}>
                        {s.value}
                    </Typography>
                    <Box sx={{
                        display: 'inline-flex', fontSize: '11.5px', fontWeight: 700, color: MUTED,
                        backgroundColor: 'var(--brand-bg, #FFFBF7)', borderRadius: '999px',
                        px: 1.25, py: 0.25, mt: 1,
                    }}>
                        {s.band}
                    </Box>
                </Box>
            ))}
        </Box>
    </Box>
);

/**
 * Both recommendation tables — identical shape for P0/P1A/P1B, only differing
 * by isNetwork (single-school Target Group column vs a network's Region +
 * School pair — see NETWORK_LEARNING_ROWS/NETWORK_INTERVENTION_ROWS above).
 * Only "Take Action" differs between plans (UnlockCapabilityDialog vs
 * RequestLearningProgramsWizard vs CreateLearningProgramDialog), so it is
 * passed in rather than hardcoded.
 */
const RecommendationsSection = ({ history, isNetwork, onTakeAction, learningRows, interventionRows }) => {
    const learningColumns = isNetwork
        ? ['Region', 'School', 'Baseline Evidence', 'Recommended Learning']
        : ['Target Group', 'Baseline Evidence', 'Recommended Learning'];
    const interventionColumns = isNetwork
        ? ['Region', 'School', 'Baseline Evidence', 'Recommended Actions']
        : ['Target Group', 'Baseline Evidence', 'Recommended Actions'];
    // "View Full List" → the dashboard's Priority Actions pages (SND / SD).
    const priorityActionsPath = isNetwork
        ? '/dq_pro/network-dashboard/priority-actions'
        : '/dq_pro/school-dashboard/priority-actions';

    return (
        <Box sx={{ ...cardSx, overflow: 'hidden' }}>
            <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2.5 }}>
                    <Typography sx={{ fontSize: '17px', fontWeight: 800, color: TEXT }}>
                        Highlighted Learning Program Recommendations
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.25 }}>
                        <Button
                            variant="outlined"
                            sx={outlineBtnSx}
                            onClick={() => history.push(`${priorityActionsPath}/learning-program`)}
                        >
                            View Full List
                        </Button>
                        <Button sx={primaryBtnSx} onClick={onTakeAction}>
                            Take Action
                        </Button>
                    </Box>
                </Box>
                <RecommendationTable columns={learningColumns} rows={learningRows} isNetwork={isNetwork} />
            </Box>

            <Box sx={{ borderTop: `1px solid ${BORDER}`, p: { xs: 2.5, sm: 3.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2.5 }}>
                    <Typography sx={{ fontSize: '17px', fontWeight: 800, color: TEXT }}>
                        Highlighted System Intervention Recommendations
                    </Typography>
                    {/* No "Take Action" here per the mockup — this section
                        is view-only. */}
                    <Button
                        variant="outlined"
                        sx={outlineBtnSx}
                        onClick={() => history.push(`${priorityActionsPath}/system-intervention`)}
                    >
                        View Full List
                    </Button>
                </Box>
                <RecommendationTable columns={interventionColumns} rows={interventionRows} isNetwork={isNetwork} />
            </Box>
        </Box>
    );
};

/**
 * P1B's "Take Action" — SW/SNW-P1B-Performance Summary-Take Action.
 *
 * Text copied verbatim from the approved mockup. Not the UnlockCapabilityDialog
 * or RequestLearningProgramsWizard flow: a P1B account already has learning_program
 * create access, so instead of requesting a program from its partner or unlocking
 * a capability, it is sent straight into LearnCreator to build one itself.
 */
export const CreateLearningProgramDialog = ({ open, onClose, onGoToLearnCreator }) => (
    <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '20px', backgroundColor: SURFACE } }}
    >
        <DialogContent sx={{ p: 3, position: 'relative' }}>
            <IconButton
                onClick={onClose}
                aria-label="Close"
                size="small"
                sx={{ position: 'absolute', top: 12, right: 12, color: MUTED }}
            >
                <CloseIcon fontSize="small" />
            </IconButton>

            <Typography sx={{ ...eyebrowSx, color: ORANGE, mb: 0.75 }}>
                Take Action
            </Typography>
            <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: TEXT, letterSpacing: '-0.2px', mb: 1.5 }}>
                Create a learning program
            </Typography>
            <Typography sx={{ fontSize: '0.9rem', color: MUTED, lineHeight: 1.6, mb: 1 }}>
                Your assessment results recommend learning objectives for specific groups.
                To build a program around any of them yourself, open LearnCreator.
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: MUTED, lineHeight: 1.6, mb: 2.5 }}>
                LearnCreator opens in a new tab — this page stays where it is.
            </Typography>

            <Box sx={{
                backgroundColor: 'var(--brand-bg, #FFFBF7)', border: `1px solid ${BORDER}`,
                borderRadius: '10px', p: 2, mb: 2.5,
            }}>
                <Typography sx={{ ...eyebrowSx, mb: 1 }}>
                    In LearnCreator you can
                </Typography>
                {[
                    'Create learning targeted to the objectives your assessment results reveal.',
                    'Preview the program before you publish.',
                    'Link it to your whole school, a grade or a class.',
                ].map((line) => (
                    <Box key={line} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75, '&:last-child': { mb: 0 } }}>
                        <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: ORANGE, mt: '7px', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '13.5px', color: TEXT, lineHeight: 1.5 }}>
                            {line}
                        </Typography>
                    </Box>
                ))}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                <Button variant="outlined" sx={outlineBtnSx} onClick={onClose}>
                    Cancel
                </Button>
                <Button sx={primaryBtnSx} onClick={onGoToLearnCreator} endIcon={<OpenInNewIcon style={{ fontSize: 16 }} />}>
                    Open LearnCreator
                </Button>
            </Box>
        </DialogContent>
    </Dialog>
);

const PerformanceSummaryPage = () => {
    useGeo(); // re-render with the API's lists once they load
    const history = useHistory();
    const { authUser } = useAuth();
    const [unlockOpen, setUnlockOpen] = useState(false);
    const [wizardOpen, setWizardOpen] = useState(false);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [wizardSchools, setWizardSchools] = useState([]);
    const [wizardClasses, setWizardClasses] = useState([]);
    const [wizardGrades, setWizardGrades] = useState([]);

    // A direct plan follows its enterprise plan's solution (solutionPlanKey).
    const solutionKey = solutionPlanKey(authUser?.dq_pro_plan?.plan_key);
    const isP0 = solutionKey === DQ_PRO_PLAN_KEY.ENTERPRISE_P0;
    const isEnterpriseP1 = solutionKey === DQ_PRO_PLAN_KEY.ENTERPRISE_P1;
    const hasLearningProgramCreate = canCreateProduct(authUser, PRODUCT_KEY.LEARNING_PROGRAM);
    // Matches DQProLearnerProgramList.page.jsx's isP1AAccount exactly — see
    // the file header. create: true is P1B; P1A is create false/absent.
    const isP1A = isEnterpriseP1 && !hasLearningProgramCreate;
    const isP1B = isEnterpriseP1 && hasLearningProgramCreate;
    const isLC = solutionKey === DQ_PRO_PLAN_KEY.ENTERPRISE_LC;
    // Network Executive Overview — now confirmed for all four tiers (P0, P1A,
    // P1B, LC all have their own SNW-*-Performance Summary mockup showing the
    // same "Network Executive Overview" title + NETWORK badge treatment), so
    // this is simply "is this a network account", not scoped by tier. P0/P1A/
    // P1B additionally swap in Region/School recommendation-table columns —
    // that part IS tier-scoped, applied only where RecommendationsSection is
    // rendered at all (LC never renders it, single-school or network).
    const isNetwork = !!authUser?.is_school_network;
    const showNetworkVariant = isNetwork;

    // Wizard picker data for P1A's "Take Action" — same loading pattern as
    // DQProLearnerProgramList.page.jsx's Request Programs wizard. All hooks
    // must run unconditionally (before the isP0/isP1A early return below), so
    // this effect is a no-op whenever isP1A is false.
    useEffect(() => {
        if (!isP1A || !authUser?.org_code) return undefined;
        let cancelled = false;
        const orLogEmpty = (what) => (e) => {
            console.warn(`[performance summary] could not load ${what}:`, e?.message || e);
            return [];
        };
        Promise.all([
            listOrgSchoolsService(authUser.org_code).catch(orLogEmpty('schools')),
            listOrgClassesService(authUser.org_code).catch(orLogEmpty('classes')),
        ]).then(([schools, classes]) => {
            if (cancelled) return;
            const schoolRows = schools || [];
            const classRows = classes || [];

            const schoolByCode = new Map(
                schoolRows
                    .filter((r) => r.institute_code)
                    .map((r) => [r.institute_code, r.institute_name || r.org_name || r.institute_code]),
            );

            setWizardSchools(schoolRows
                .filter((r) => r.institute_name || r.org_name)
                .map((r) => ({
                    value: r.institute_name || r.org_name,
                    label: r.institute_name || r.org_name,
                    institute_code: r.institute_code || '',
                })));

            setWizardClasses(classRows
                .filter((r) => r.section_name)
                .map((r) => {
                    const school = schoolByCode.get(r.institute_code);
                    return {
                        value: r.section_name,
                        label: school ? `${r.section_name} · ${school}` : r.section_name,
                        institute_code: r.institute_code || '',
                    };
                }));

            setWizardGrades([...new Set(
                classRows.map((r) => String(r.section_level || '').trim()).filter(Boolean),
            )].sort().map((g) => ({ value: g, label: g, institute_code: '' })));
        });
        return () => { cancelled = true; };
    }, [isP1A, authUser?.org_code]);

    // The dashboard this page summarises (see the file header): one request,
    // only the sections it reads. A failure leaves every value "—".
    const [dash, setDash] = useState(null);
    /*
     * The school's or network's own name and country, read the way Settings
     * reads them: the caller's own organisation row — for a school inside a
     * network, the school's row — and the ISO country code shown as a name.
     * The School dashboard's header card carries no country (Ian-SW-16Oct-1),
     * and a workspace with no results yet gets no header card at all, which
     * left even the name and country "—" (LA-SNW-19Oct-2).
     */
    const [orgInfo, setOrgInfo] = useState({ name: '', country: '' });
    useEffect(() => {
        if (!authUser?.org_code) return undefined;
        let cancelled = false;
        getOrgDetailsService(authUser.org_code)
            .then((res) => {
                const d = res?.data;
                const org = (d?.org && typeof d.org === 'object') ? d.org
                    : (d?.data?.org && typeof d.data.org === 'object') ? d.data.org : null;
                const code = String(org?.org_country || '').trim();
                const country = (findCountry(code) || {}).label || code;
                if (!cancelled) setOrgInfo({ name: String(org?.org_name || '').trim(), country });
            })
            .catch(() => { if (!cancelled) setOrgInfo({ name: '', country: '' }); });
        return () => { cancelled = true; };
    }, [authUser?.org_code]);
    const sections = isNetwork ? SECTIONS.network : SECTIONS.school;
    useEffect(() => {
        if (!isP0 && !isP1A && !isP1B && !isLC) return undefined;
        let cancelled = false;
        const fetchDashboard = isNetwork ? getSchoolNetworkDashboardService : getSchoolDashboardService;
        fetchDashboard({ card_sections: [sections.overview, sections.reassess, sections.summary].join(',') })
            .then((res) => { if (!cancelled) setDash(res?.data || null); })
            .catch((e) => {
                console.warn('[performance summary] could not load the dashboard:', e?.message || e);
                if (!cancelled) setDash(null);
            });
        return () => { cancelled = true; };
    }, [isNetwork, isP0, isP1A, isP1B, isLC, sections.overview, sections.reassess, sections.summary]);

    if (!isP0 && !isP1A && !isP1B && !isLC) {
        return <WorkspacePlaceholder title="Performance Summary" />;
    }

    /**
     * Same one-request-per-objective submission DQProLearnerProgramList.page
     * .jsx already uses for this exact wizard (its sendProgramRequests) —
     * reused verbatim rather than re-derived, since it is the established,
     * already-working contract for RequestLearningProgramsWizard's onSend.
     *
     * EXTENDED for a network's multi-school "School(s)" selection: per
     * explicit instruction, choosing several schools for one objective sends
     * ONE request covering all of them, as `institute_codes` (plural, an
     * array) — NOT confirmed against the backend. `/dq_insider/dqin/client/
     * program-request/create` has only ever been sent the singular
     * `institute_code` before this; whether it accepts/handles an
     * `institute_codes` array has not been checked against its implementation
     * (out of scope of this frontend repo). Falls back to the existing
     * singular `institute_code` for every single-school selection, which is
     * unaffected.
     */
    const sendProgramRequests = async (objectives, details) => {
        try {
            await Promise.all(objectives.map((topic, i) => {
                const d = details[i] || {};
                const isClass = d.groupType === 'Class(es)';
                const isGrade = d.groupType === 'Grade(s)';
                const isSchool = d.groupType === 'School(s)';
                const multiSchoolCodes = (isSchool && Array.isArray(d.groupInstituteCodes) && d.groupInstituteCodes.length)
                    ? d.groupInstituteCodes
                    : null;
                return createProgramRequestService({
                    topic,
                    num_sessions: Number(d.sessions) || undefined,
                    session_duration_mins: Number(d.minutes) || undefined,
                    additional_info: String(d.additional_info || '').trim() || undefined,
                    grade_level: isGrade ? (d.groupValue || '') : '',
                    class_name: isClass ? (d.groupValue || '') : '',
                    ...(multiSchoolCodes
                        ? { institute_codes: multiSchoolCodes }
                        : { institute_code: (isClass || isSchool) ? (d.groupInstituteCode || '') : '' }),
                });
            }));
            toast(TOAST_TYPES.SUCCESS, 'Requests sent', `${objectives.length} program request${objectives.length === 1 ? '' : 's'} sent to your partner.`);
        } catch (e) {
            toast(TOAST_TYPES.ERROR, 'Could not send requests', e?.message || 'Please try again.');
        }
    };

    // ── Values from the dashboard (see the file header). "—" where it has none.
    const orgCard = findCard(dash, sections.overview, 'C_2');
    const composite = findCard(dash, sections.overview, 'C_3');
    const anyRisk = findCard(dash, sections.overview, 'C_4');
    const participation = findCard(dash, sections.overview, 'C_5');
    const contextMove = findCard(dash, sections.reassess, 'C_4');   // Context & Support composite
    const compositeMove = findCard(dash, sections.reassess, 'C_2');
    const riskMove = findCard(dash, sections.reassess, 'C_3');
    const reassessKpi = findCard(dash, sections.reassess, 'C_5');   // Reassessment Duration
    const programsCreated = findCard(dash, sections.summary, 'C_2');
    const unitsAssigned = findCard(dash, sections.summary, 'C_3');
    const impactRate = findCard(dash, sections.summary, 'C_5');

    const schoolName = orgColumn(orgCard, 'SCHOOL') || orgInfo.name || '—';
    const networkName = orgColumn(orgCard, 'NETWORK') || orgInfo.name || '—';
    const infoBar = {
        country: orgColumn(orgCard, 'COUNTRY') || orgInfo.country || '—',
        baseline: orgColumn(orgCard, 'ASSESSMENT WINDOW') || '—',
        learning: '—',
        reassess: kpiText(reassessKpi?.kpi_value),
    };

    const participants = whole(participation?.kpi_data?.participants);
    const registered = whole(participation?.kpi_data?.total);
    const statCards = [
        { label: 'Readiness Composite Score', value: kpiText(composite?.kpi_value), band: composite?.kpi_band || '—' },
        { label: 'Cyber Risk Experience', value: kpiText(anyRisk?.kpi_value, true), band: anyRisk?.kpi_band || '—' },
        {
            label: 'Participation',
            value: kpiText(participation?.kpi_value, true),
            band: participants !== null && registered !== null ? `${participants} / ${registered}` : '—',
        },
        { label: 'Context & Support', value: kpiText(contextMove?.baseline?.value), band: contextMove?.baseline?.band || '—' },
    ];

    // "1 of 15" → 1 · "of 15 priorities"; "2/2" → 2/2 · "100% coverage".
    const createdMatch = String(programsCreated?.kpi_value ?? '').match(/^\s*(\d+)\s+of\s+(\d+)\s*$/);
    const assignedMatch = String(unitsAssigned?.kpi_value ?? '').match(/^\s*(\d+)\s*\/\s*(\d+)\s*$/);
    // The second card's LABEL differs: "Classes Assigned" for a school,
    // "Schools Assigned" for a network (the data's own names).
    const learningImpactCards = [
        {
            label: 'Programs Created',
            value: createdMatch ? createdMatch[1] : kpiText(programsCreated?.kpi_value),
            band: createdMatch ? `of ${createdMatch[2]} priorities` : 'of priorities',
        },
        {
            label: showNetworkVariant ? 'Schools Assigned' : 'Classes Assigned',
            value: kpiText(unitsAssigned?.kpi_value),
            band: assignedMatch && Number(assignedMatch[2]) > 0
                ? `${Math.round((Number(assignedMatch[1]) / Number(assignedMatch[2])) * 100)}% coverage`
                : 'coverage',
        },
        { label: 'Priority Curriculum Coverage', value: '—', band: 'Outcomes mapped' },
        { label: 'Avg Impact Rate', value: kpiText(impactRate?.kpi_value, true), band: 'Learning signal' },
    ];

    // A MovementCard's change once the reassessment is in (its own delta, or
    // reassess − baseline); "—" while it is pending.
    const moveOf = (card) => {
        if (!card || card.status === 'pending') return '—';
        const d = whole(card.delta) ?? (whole(card.reassess?.value) !== null && whole(card.baseline?.value) !== null
            ? whole(card.reassess.value) - whole(card.baseline.value) : null);
        if (d === null) return '—';
        const unit = card.unit === 'pp' ? '%p' : (card.unit ? ` ${card.unit}` : '');
        return `${d > 0 ? '+' : ''}${d}${unit}`;
    };
    const movementBand = (card, active) => (!card || card.status === 'pending' ? active : 'since baseline');
    const cosMovementCards = [
        {
            label: 'Reassessment',
            value: kpiText(reassessKpi?.kpi_value),
            // Done: the cycle it was (e.g. "2026 (cycle 2)"); otherwise still ahead.
            band: reassessKpi?.status === 'completed' ? (reassessKpi?.kpi_data?.cycle_label || 'completed') : 'next COS cycle',
        },
        { label: 'Composite Move', value: moveOf(compositeMove), band: movementBand(compositeMove, 'awaiting reassess') },
        { label: 'Risk Reduction', value: moveOf(riskMove), band: movementBand(riskMove, 'awaiting reassess') },
        { label: 'Context Move', value: moveOf(contextMove), band: movementBand(contextMove, 'support active') },
    ];

    // The two highlighted tables (the summary section's C_6 / C_7).
    const rowSpec = (recommendationColumn) => (showNetworkVariant
        ? { region: 'Region', school: 'School', evidence: 'Baseline Evidence', recommendation: recommendationColumn }
        : { targetGroup: 'Target Group', evidence: 'Baseline Evidence', recommendation: recommendationColumn });
    const learningRows = rowsFrom(findCard(dash, sections.summary, 'C_6'), rowSpec('Recommended Learning'));
    const interventionRows = rowsFrom(findCard(dash, sections.summary, 'C_7'), rowSpec('Recommended Actions'));

    return (
        <Box sx={{ width: '100%', maxWidth: '100%', px: { xs: 2, sm: 0 }, py: { xs: 3, sm: 5 } }}>
            <Typography sx={{ fontSize: { xs: '24px', sm: '30px' }, fontWeight: 800, color: TEXT, mb: 3.5 }}>
                {showNetworkVariant ? 'Network Executive Overview' : 'School Executive Overview'}
            </Typography>

            {/* Info bar */}
            <Box sx={{
                ...cardSx,
                display: 'flex', flexWrap: 'wrap',
                p: { xs: '18px 20px', sm: '22px 28px' }, mb: 3,
            }}>
                {[
                    ['School', schoolName],
                    ['Country', infoBar.country],
                    ['Baseline', infoBar.baseline],
                    ['Learning', infoBar.learning],
                    ['Reassess', infoBar.reassess],
                ].map(([label, value], i) => (
                    <Box
                        key={label}
                        sx={{
                            flex: { xs: '1 1 40%', sm: 1 },
                            minWidth: { xs: '45%', sm: 0 },
                            pr: { sm: 3 },
                            pl: { sm: i === 0 ? 0 : 3 },
                            py: { xs: 1, sm: 0 },
                            borderLeft: { sm: i === 0 ? 'none' : `1px solid ${BORDER}` },
                        }}
                    >
                        {/* Network Executive Overview's first segment is a
                            NETWORK badge + the network's name, in place of a
                            "School" label — same NETWORK badge styling already
                            used on DQProClientList.page.jsx's network rows. */}
                        {i === 0 && showNetworkVariant ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box component="span" sx={{
                                    fontSize: 10, fontWeight: 700, letterSpacing: '0.5px',
                                    color: ORANGE, border: `1px solid ${ORANGE}`,
                                    borderRadius: '4px', px: 0.75, py: '1px', whiteSpace: 'nowrap',
                                }}>
                                    NETWORK
                                </Box>
                                <Typography sx={infoValueSx}>{networkName}</Typography>
                            </Box>
                        ) : (
                            <>
                                <Typography sx={infoLabelSx}>{label}</Typography>
                                <Typography sx={infoValueSx}>{value}</Typography>
                            </>
                        )}
                    </Box>
                ))}
            </Box>

            {/* Baseline stat cards — P0, P1A, P1B. LC mutes this section (see
                file header) so it is never shown for LC. */}
            {(isP0 || isP1A || isP1B) && (
                <Box sx={{ mb: 4 }}>
                    <StatCardsRow eyebrow="Baseline" cards={statCards} />
                </Box>
            )}

            {/*
              * Recommendations — the P1A/P1B mockups explicitly annotate
              * "Add this table in between 'Baseline' and 'Learning Impact'",
              * so this same section sits right here for both; P0 simply has
              * nothing below it. LC mutes this section entirely.
              */}
            {(isP0 || isP1A || isP1B) && (
                <Box sx={{ mb: (isP1A || isP1B) ? 4 : 0 }}>
                    <RecommendationsSection
                        history={history}
                        isNetwork={showNetworkVariant}
                        learningRows={learningRows}
                        interventionRows={interventionRows}
                        onTakeAction={() => {
                            if (isP1A) setWizardOpen(true);
                            else if (isP1B) setCreateDialogOpen(true);
                            else setUnlockOpen(true);
                        }}
                    />
                </Box>
            )}

            {/* Learning Impact — shown on P1A, P1B and LC. */}
            {(isP1A || isP1B || isLC) && (
                <Box sx={{ mb: isLC ? 0 : 4 }}>
                    <StatCardsRow eyebrow="Learning Impact" cards={learningImpactCards} />
                </Box>
            )}

            {/* COS Movement — P1A/P1B only. LC mutes this section (see file header). */}
            {(isP1A || isP1B) && (
                <>
                    <Box sx={{ mb: 1.5 }}>
                        <StatCardsRow eyebrow="COS Movement" cards={cosMovementCards} />
                    </Box>
                    <Typography sx={{ fontSize: '12px', fontStyle: 'italic', color: MUTED }}>
                        Movement confirms only after reassessment — learning signal above is intervention evidence, not confirmed improvement.
                    </Typography>
                </>
            )}

            {isP0 && (
                <UnlockCapabilityDialog
                    open={unlockOpen}
                    onClose={() => setUnlockOpen(false)}
                    authUser={authUser}
                />
            )}

            {isP1A && (
                // SW/SNW-P1A-Performance Summary-Take Action.
                <RequestLearningProgramsWizard
                    open={wizardOpen}
                    onClose={() => setWizardOpen(false)}
                    partnerName={authUser?.partner_name || 'your DQ Partner'}
                    schools={wizardSchools}
                    classes={wizardClasses}
                    grades={wizardGrades}
                    isNetwork={isNetwork}
                    onSend={sendProgramRequests}
                />
            )}

            {isP1B && (
                // SW/SNW-P1B-Performance Summary-Take Action.
                <CreateLearningProgramDialog
                    open={createDialogOpen}
                    onClose={() => setCreateDialogOpen(false)}
                    onGoToLearnCreator={() => {
                        setCreateDialogOpen(false);
                        openLearnCreator(history);   // the LearnCreator landing page, in a new tab (QA 9 Oct, LA-SD-5)
                    }}
                />
            )}
        </Box>
    );
};

export default PerformanceSummaryPage;
